import React, { type FC } from 'react';
import {
  ImageHoverEffectOptions as HoverEffectOptions,
  ImageLoadingBehaviorOptions as LoadingBehaviorOptions,
  ImageResizeOptions as ResizeOptions,
  ThumbnailImage,
} from 'wix-ui-tpa/cssVars';
import { ImageResizeOptions } from '../../../../../../types/types';
import { useWidgetViewModel } from '../../../hooks/useWidgetViewModel';
import { classes, st } from './LayoutImage.st.css';
import { DataHooks } from './consts';
import { Image } from '@wix/ambassador-bookings-services-v2-service/types';
import { sdk as imageSDK } from '@wix/image-kit';

export type LayoutImageProps = {
  image?: Image;
  imageTitle: string;
  width: number;
  height: number;
  onClick(): void;
  className?: string;
  hrefUrl?: string;
};

const mapImageResizeOptionsToResizeOptions = {
  [ImageResizeOptions.CROP]: ResizeOptions.cover,
  [ImageResizeOptions.FIT]: ResizeOptions.contain,
};

export const LayoutImage: FC<LayoutImageProps> = (props) => {
  const { image, onClick, className, hrefUrl } = props;
  const { isSEO, serviceImageViewModel } = useWidgetViewModel();
  const { imageResize, focalPoint } = serviceImageViewModel;
  const { height = 0, width = 9, url = '' } = image ?? {};

  // We cannot pass image.url directly to ThumbnailImage src prop because the image url might be in an old format (e.g. prefixed by "media/"),
  // Which ThumbnailImage doesn't handle properly. The image SDK methods normalize the URL so it is valid for ThumbnailImage.
  const imageUrl =
    imageResize === ImageResizeOptions.CROP
      ? imageSDK.getScaleToFillImageURL(url, width, height, width, height, {
          focalPoint,
        })
      : imageSDK.getScaleToFitImageURL(url, width, height, width, height);

  return (
    <div data-hook={DataHooks.ROOT} className={st(classes.root, className)}>
      <div data-hook={DataHooks.WRAPPER} className={st(classes.wrapper)}>
        <div
          data-hook={DataHooks.CONTAINER}
          onClick={onClick}
          className={classes.clickable}
          role="presentation"
          tabIndex={-1}
        >
          {image ? (
            <a
              href={hrefUrl ?? '#'}
              data-hook={DataHooks.LINK}
              className={classes.link}
            >
              <ThumbnailImage
                alt={image.altText || undefined}
                fluid
                width={isSEO ? image.width : 50}
                height={isSEO ? image.height : 50}
                focalPoint={focalPoint}
                loadingBehavior={LoadingBehaviorOptions.none}
                hoverEffect={HoverEffectOptions.darken}
                resize={mapImageResizeOptionsToResizeOptions[imageResize]}
                data-hook={DataHooks.IMAGE}
                className={classes.image}
                src={imageUrl}
                sourceHeight={image.height}
                sourceWidth={image.width}
                shouldUseLQIP
                isSEOBot={isSEO}
              />
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
};
